/* DFN Soil Calculator: wizard, math, product mapping, PDF.
 * Bootstrapped from sections/soil-calculator.liquid via window.DFN_SOIL_CALC.
 */
(function () {
  'use strict';

  const CFG = window.DFN_SOIL_CALC || {};
  const DATA_URL = CFG.dataUrl;
  const LOGO_URL = CFG.logoUrl;
  const SHOP_DOMAIN = (CFG.shopDomain || 'https://dragonflyfarmlanglois.com').replace(/\/$/, '');
  const CONTACT = CFG.contact || {};

  const STATE = {
    step: 'project',
    answers: {
      projectType: null,
      length_ft: null,
      width_ft: null,
      depth_in: null,
      sqft: null,
      pot_input_method: 'dims',
      pot_diameter_in: null,
      pot_height_in: null,
      pot_gallons: null,
      pot_count: 1,
      plant_pot_size: null,
      grow_what: null,
      soil_issue: null,
      topdress_goal: null,
      mulch_use: null,
      specialty_kind: null,
      hole_kind: null,
      pot_style: 'standard',
    },
    data: null,
    plan: null,
  };

  // -------- Project type definitions --------
  // Each project owns its volume math, its input step renderer, and its
  // product mapping. The mapping is the canonical Jaymi-approved soil
  // recommendations from PROPOSAL.md.

  const PROJECT_TYPES = {
    raised_bed_new: {
      id: 'raised_bed_new',
      label: 'New raised bed',
      desc: 'Filling a fresh bed from the bottom up.',
      needsGrowWhat: true,
      inputs: ['length_ft', 'width_ft', 'depth_in'],
      defaults: { depth_in: 12 },
      summary: (a) => `${fmt(a.length_ft)} ft × ${fmt(a.width_ft)} ft × ${fmt(a.depth_in)} in deep, for ${growLabel(a.grow_what)}`,
      computeVolume: (a) => a.length_ft * a.width_ft * (a.depth_in / 12) * 1.1,
      mapping: (a) => {
        // 80% primary fill + 20% top planting layer
        if (a.grow_what === 'flower') {
          return {
            primary: { handle: 'gandb-raised-bed-and-potting-soil-2cf', fraction: 0.8, role: 'Main fill', note: 'Fill the bottom 80 percent of the bed.' },
            secondary: { handle: 'g-and-b-soil-building-conditioner', fraction: 0.2, role: 'Top planting layer', prefer_variant_cf: 3, note: 'Mix into the top 6 inches where you plant.' },
            why: 'A nutrient-rich raised bed mix on the bottom plus a lighter soil-building conditioner on top gives ornamentals great drainage and easy root run.',
          };
        }
        // veggies or mixed
        return {
          primary: { handle: 'garden-valley-naturals-soil-conditioner-and-raised-bed', fraction: 0.8, role: 'Main fill', note: 'Fill the bottom 80 percent of the bed.' },
          secondary: { handle: 'gv-garden-and-veg-mix-w-bio-amendment', fraction: 0.2, role: 'Top planting layer', note: 'Mix into the top 6 inches. This is where most veggie roots feed.' },
          why: 'For edibles, we like a local Rexius raised bed base topped with a biochar-rich veggie amendment. The amendment has the slow-release nutrition that fruiting crops crave.',
          fallback_primary: { handle: 'gandb-raised-bed-and-potting-soil-2cf' },
        };
      },
    },

    raised_bed_topup: {
      id: 'raised_bed_topup',
      label: 'Top up an existing raised bed',
      desc: 'Adding a few inches to refresh a bed for the season.',
      needsGrowWhat: true,
      inputs: ['length_ft', 'width_ft', 'depth_in'],
      defaults: { depth_in: 2 },
      summary: (a) => `Refreshing a ${fmt(a.length_ft)} ft × ${fmt(a.width_ft)} ft bed with ${fmt(a.depth_in)} in of new soil, for ${growLabel(a.grow_what)}`,
      computeVolume: (a) => a.length_ft * a.width_ft * (a.depth_in / 12),
      mapping: (a) => {
        const sec = a.grow_what === 'flower'
          ? { handle: 'gandb-purely-compost', fraction: 0.4, role: 'Compost boost', note: 'Layer on top for a quick nutrient lift before planting.' }
          : { handle: 'gv-garden-and-veg-mix-w-bio-amendment', fraction: 0.4, role: 'Veggie booster', note: 'Mix into the top few inches for the new season.' };
        return {
          primary: { handle: 'g-and-b-soil-building-conditioner', fraction: 0.6, role: 'Refresh layer', prefer_variant_cf: 3, note: 'Spread this as the main top-up layer.' },
          secondary: sec,
          why: 'Soil-building conditioner replaces what was lost to last year\'s harvest, and a smaller dose of compost or veggie amendment kick-starts the new growing season.',
        };
      },
    },

    in_ground_amend: {
      id: 'in_ground_amend',
      label: 'Amend an in-ground bed',
      desc: 'Improving the native soil in a garden bed before planting.',
      needsGrowWhat: false,
      needsSoilIssue: true,
      inputs: ['sqft'],
      defaults: {},
      summary: (a) => `Amending ${fmt(a.sqft)} sq ft of in-ground bed (${soilIssueLabel(a.soil_issue)})`,
      computeVolume: (a) => a.sqft * 0.25, // 3 inches tilled in
      mapping: (a) => {
        if (a.soil_issue === 'clay') {
          return {
            primary: { handle: 'g-and-b-soil-building-conditioner', fraction: 1.0, role: 'Soil amendment', prefer_variant_cf: 3, note: 'Till in 3 inches across the bed.' },
            secondary_per_sqft: { handle: 'gandb-pumice', sqft_per_bag: 25, role: 'Drainage', note: 'Pumice opens up heavy clay so roots and water can move.' },
            why: 'Heavy clay soils trap water and suffocate roots. Soil-building conditioner adds organic structure and pumice opens up the drainage.',
          };
        }
        if (a.soil_issue === 'sandy') {
          return {
            primary: { handle: 'garden-valley-organic-compost', fraction: 1.0, role: 'Soil amendment', note: 'Till in 3 inches across the bed.' },
            secondary_per_sqft: { handle: 'black-gold-peat-moss-plus', sqft_per_bag: 30, role: 'Moisture retention', note: 'Peat holds onto water and nutrients in sandy soils that drain too fast.' },
            why: 'Sandy soils drain too fast for most plants. Compost adds body and peat moss helps hold onto water and feed.',
          };
        }
        return {
          primary: { handle: 'gandb-purely-compost', fraction: 1.0, role: 'Compost', note: 'Till in 3 inches across the bed.' },
          why: 'A generous layer of pure compost is the simplest way to feed a bed that just needs more life and nutrition.',
          fallback_primary: { handle: 'garden-valley-organic-compost' },
        };
      },
    },

    topdress: {
      id: 'topdress',
      label: 'Top-dress with compost or topsoil',
      desc: 'Spreading a thin layer on top of beds, lawns, or low spots.',
      needsGrowWhat: false,
      needsTopdressGoal: true,
      inputs: ['sqft', 'depth_in'],
      defaults: { depth_in: 1 },
      summary: (a) => `${fmt(a.sqft)} sq ft top-dress, ${fmt(a.depth_in)} in deep (${topdressGoalLabel(a.topdress_goal)})`,
      computeVolume: (a) => a.sqft * (a.depth_in / 12),
      mapping: (a) => {
        const map = {
          topsoil: { handle: 'garden-valley-top-soil', why: 'Garden Valley Top Soil is local screened soil, ideal for filling low spots or leveling ground.' },
          compost: { handle: 'garden-valley-organic-compost', why: 'Garden Valley Organic Compost is our everyday workhorse for top-dressing beds and lawns.' },
          premium_compost: { handle: 'seacoast-compost-organic', why: 'Seacoast Compost is a premium organic option, made locally on the Oregon coast.' },
          manure: { handle: 'g-and-b-gardeners-steer-manure', why: 'Steer manure adds nutrients for vegetable beds, roses, and other heavy feeders.' },
        };
        const pick = map[a.topdress_goal] || map.compost;
        return {
          primary: { handle: pick.handle, fraction: 1.0, role: topdressGoalLabel(a.topdress_goal), note: 'Spread evenly across the area at the depth you chose.' },
          why: pick.why,
        };
      },
    },

    pots: {
      id: 'pots',
      label: 'Fill pots or containers',
      desc: 'Working out potting soil for one or many containers.',
      needsGrowWhat: false,
      needsPotInputs: true,
      inputs: [],
      defaults: { pot_count: 1, pot_style: 'standard' },
      summary: (a) => {
        const ptStyle = potStyleLabel(a.pot_style);
        const count = a.pot_count > 1 ? `${a.pot_count} pots` : '1 pot';
        if (a.pot_input_method === 'gallons') {
          return `${count} (${fmt(a.pot_gallons)} gal each), ${ptStyle}`;
        }
        return `${count} (${fmt(a.pot_diameter_in)} in wide × ${fmt(a.pot_height_in)} in tall), ${ptStyle}`;
      },
      computeVolume: (a) => {
        let perPotCf;
        if (a.pot_input_method === 'gallons') {
          perPotCf = a.pot_gallons * 0.1337;
        } else {
          const r = a.pot_diameter_in / 2;
          perPotCf = Math.PI * r * r * a.pot_height_in / 1728 * 0.85;
        }
        return perPotCf * (a.pot_count || 1);
      },
      mapping: (a) => {
        if (a.pot_style === 'moisture') {
          return {
            primary: { handle: 'black-gold-moisture-supreme-container-mix', fraction: 1.0, role: 'Moisture-retaining mix', note: 'Designed for hanging baskets and sun-exposed pots that dry out fast.' },
            why: 'Moisture Supreme holds water longer than a standard mix, which is exactly what hanging baskets and full-sun containers need.',
          };
        }
        if (a.pot_style === 'premium') {
          return {
            primary: { handle: 'seacoast-potting-soil', fraction: 1.0, role: 'Premium potting soil', note: 'A small-batch, local premium mix.' },
            why: 'Seacoast is our top-shelf locally made potting soil, worth it for prized plants and showcase containers.',
          };
        }
        // standard: pick small bag for small jobs, bigger bag for bigger jobs
        const totalCf = PROJECT_TYPES.pots.computeVolume(a);
        if (totalCf <= 1.0) {
          return {
            primary: { handle: 'gandb-organics-potting-soil', fraction: 1.0, role: 'Organic potting soil', note: 'Smaller bag, easy for a pot or two.' },
            why: 'G&B Organics is our reliable everyday potting soil. The 8-quart bag is just right for small jobs.',
          };
        }
        return {
          primary: { handle: 'black-gold-potting-soil', fraction: 1.0, role: 'All-purpose potting soil', prefer_variant_cf: 1.5, note: 'Bigger bag, fewer trips for filling several pots.' },
          why: 'For more than a pot or two, the 1.5 cubic foot bag of Black Gold gives you the best value.',
        };
      },
    },

    planting_hole: {
      id: 'planting_hole',
      label: 'Planting hole (tree or shrub)',
      desc: 'Backfill amendment for a single new tree or shrub.',
      needsGrowWhat: false,
      needsPlantPotSize: true,
      inputs: [],
      defaults: { plant_pot_size: 5 },
      summary: (a) => `Backfill for a ${fmt(a.plant_pot_size)}-gallon plant${a.hole_kind === 'acid' ? ' (acid-lover)' : ''}`,
      computeVolume: (a) => {
        return a.plant_pot_size * 0.1337 * 1.0;
      },
      mapping: (a) => {
        if (a.hole_kind === 'acid') {
          return {
            primary: { handle: 'gandb-acid-planting-mix', fraction: 1.0, role: 'Acid-lover backfill', note: 'Mix 50/50 with the soil you dug out of the hole.' },
            why: 'Rhododendrons, blueberries, camellias, and other acid-lovers need a low-pH soil. This is our go-to mix for the planting hole.',
          };
        }
        return {
          primary: { handle: 'g-and-b-soil-building-conditioner', fraction: 1.0, role: 'Backfill amendment', prefer_variant_cf: 1.5, note: 'Mix 50/50 with the soil you dug out of the hole.' },
          secondary_per_plant: { handle: 'garden-valley-hemlock-mulch', bags_per_plant: 1, role: 'Top mulch', note: 'A 2 to 3 inch ring of mulch keeps roots cool and weed-free.' },
          why: 'A 50/50 mix of soil-building conditioner with your native soil gives roots an easy place to start, then a ring of hemlock mulch on top holds moisture and protects the crown.',
        };
      },
    },

    mulch: {
      id: 'mulch',
      label: 'Mulch beds or paths',
      desc: 'Bark mulch for beds, walkways, or play areas.',
      needsGrowWhat: false,
      needsMulchUse: true,
      inputs: ['sqft', 'depth_in'],
      defaults: { depth_in: 2 },
      summary: (a) => `${fmt(a.sqft)} sq ft of ${mulchUseLabel(a.mulch_use)}, ${fmt(a.depth_in)} in deep`,
      computeVolume: (a) => a.sqft * (a.depth_in / 12),
      mapping: (a) => {
        if (a.mulch_use === 'budget') {
          return {
            primary: { handle: 'gardeners-hemlock-bark-mulch', fraction: 1.0, role: 'Mulch (budget)', note: 'Spread evenly at your chosen depth.' },
            why: 'Gardener\'s Hemlock Bark is our best value bark mulch for covering bigger areas.',
          };
        }
        return {
          primary: { handle: 'garden-valley-hemlock-mulch', fraction: 1.0, role: 'Hemlock mulch', note: 'Spread evenly at your chosen depth.' },
          why: 'Garden Valley Hemlock is our most-recommended mulch. Local, slow to break down, and looks great in any bed.',
        };
      },
    },

    specialty: {
      id: 'specialty',
      label: 'Specialty soil',
      desc: 'Orchids, cactus, acid-lovers, seed starting, and more.',
      needsGrowWhat: false,
      needsSpecialtyKind: true,
      inputs: ['pot_count'],
      defaults: { pot_count: 1 },
      summary: (a) => `${specialtyKindLabel(a.specialty_kind)}, enough for ${fmt(a.pot_count)} pot${a.pot_count > 1 ? 's' : ''}`,
      computeVolume: (a) => {
        // Specialty is bag-count driven, not volume-driven. We pick a sensible
        // pot volume per use case and multiply.
        const perPotCf = {
          orchid: 0.05,           // most orchids in 4-6 in pots
          cactus: 0.04,
          cactus_citrus_palm: 0.15,
          african_violet: 0.02,
          seed_starting: 0.15,    // small flats
        };
        return (perPotCf[a.specialty_kind] || 0.1) * (a.pot_count || 1);
      },
      mapping: (a) => {
        const map = {
          orchid: { handle: 'gandb-potting-soil-orchid-mix', why: 'A bark-and-perlite mix that orchids love. Keep them on the dry side and they\'ll bloom again.' },
          cactus: { handle: 'black-gold-cactus-mix', why: 'Fast-draining for cactus and succulents. Never let them sit in wet soil.' },
          cactus_citrus_palm: { handle: 'gandb-palm-citrus-cactus-soil', why: 'A bigger-bag option for palms, citrus, and cactus that need more soil than a tiny succulent pot.' },
          african_violet: { handle: 'black-gold-african-violet-potting-mix', why: 'Light and slightly acidic. Bottom-water your violets and they\'ll thrive.' },
          seed_starting: { handle: 'gandb-organics-seed-starter', why: 'Fine-textured and sterile. Gives seedlings the gentle start they need.' },
        };
        const pick = map[a.specialty_kind] || map.orchid;
        return {
          primary: { handle: pick.handle, fraction: 1.0, role: specialtyKindLabel(a.specialty_kind), note: 'Bag count is based on a typical pot size for this use.' },
          why: pick.why,
        };
      },
    },
  };

  // -------- Label helpers --------
  function fmt(n) {
    if (n == null) return '';
    if (Number.isInteger(n)) return String(n);
    return String(Math.round(n * 100) / 100);
  }
  function growLabel(g) {
    return { veg: 'veggies and herbs', flower: 'flowers and ornamentals', mixed: 'a mix of both' }[g] || 'planting';
  }
  function soilIssueLabel(s) {
    return { clay: 'heavy clay', sandy: 'sandy / drains too fast', nutrients: 'needs more nutrients' }[s] || 'amending';
  }
  function topdressGoalLabel(g) {
    return { topsoil: 'Topsoil', compost: 'Compost', premium_compost: 'Premium compost', manure: 'Steer manure' }[g] || 'Top-dress';
  }
  function potStyleLabel(p) {
    return { standard: 'standard potting mix', moisture: 'moisture-retaining mix', premium: 'premium potting mix' }[p] || 'potting mix';
  }
  function mulchUseLabel(m) {
    return { hemlock: 'hemlock mulch', budget: 'budget bark mulch' }[m] || 'mulch';
  }
  function specialtyKindLabel(k) {
    return {
      orchid: 'Orchid mix',
      cactus: 'Cactus and succulent mix',
      cactus_citrus_palm: 'Palm / citrus / cactus mix',
      african_violet: 'African violet mix',
      seed_starting: 'Seed starting mix',
    }[k] || 'Specialty mix';
  }

  // -------- Product resolution --------
  function findProduct(handle) {
    if (!STATE.data || !STATE.data.products) return null;
    return STATE.data.products[handle] || null;
  }

  function pickVariant(product, prefer_cf) {
    if (!product || !product.variants || !product.variants.length) return null;
    // Try preferred size first if specified
    if (prefer_cf) {
      const match = product.variants.find(v => v.size_cf && Math.abs(v.size_cf - prefer_cf) < 0.01);
      if (match) return match;
    }
    // Prefer an available variant. Among available, prefer the largest cf.
    const available = product.variants.filter(v => v.available && v.size_cf);
    if (available.length) {
      return available.sort((a, b) => b.size_cf - a.size_cf)[0];
    }
    // No available variants: return the largest variant overall so we can
    // still show coverage info, but flag oos in caller.
    const withSize = product.variants.filter(v => v.size_cf);
    if (withSize.length) return withSize.sort((a, b) => b.size_cf - a.size_cf)[0];
    return product.variants[0];
  }

  function resolveRec(rec_spec, volume_cf, opts) {
    opts = opts || {};
    if (!rec_spec) return null;
    let product = findProduct(rec_spec.handle);
    let variant = pickVariant(product, rec_spec.prefer_variant_cf);

    let oos = false;
    let fallbackUsed = false;

    if (!product || !variant || !variant.available) {
      oos = true;
      if (rec_spec.fallback_handle || opts.fallback_handle) {
        const fbHandle = rec_spec.fallback_handle || opts.fallback_handle;
        const fbProduct = findProduct(fbHandle);
        const fbVariant = pickVariant(fbProduct);
        if (fbProduct && fbVariant && fbVariant.available) {
          product = fbProduct;
          variant = fbVariant;
          oos = false;
          fallbackUsed = true;
        }
      }
    }

    if (!product || !variant) return null;

    const cfNeeded = rec_spec.fraction != null ? volume_cf * rec_spec.fraction : volume_cf;
    const bags = variant.size_cf > 0 ? Math.ceil(cfNeeded / variant.size_cf) : 0;
    const price = parseFloat(variant.price) || 0;
    const subtotal = bags * price;

    return {
      handle: product.handle,
      title: product.title,
      url: SHOP_DOMAIN + product.url,
      vendor: product.vendor,
      variant_title: variant.title,
      size_cf: variant.size_cf,
      sku: variant.sku,
      available: variant.available,
      price: price,
      bags: bags,
      cf_covered: bags * variant.size_cf,
      cf_needed: cfNeeded,
      subtotal: subtotal,
      role: rec_spec.role || '',
      note: rec_spec.note || '',
      oos: oos,
      fallback_used: fallbackUsed,
    };
  }

  function resolvePerSqft(rec_spec, sqft) {
    if (!rec_spec) return null;
    const product = findProduct(rec_spec.handle);
    const variant = pickVariant(product);
    if (!product || !variant) return null;
    const bags = Math.ceil(sqft / rec_spec.sqft_per_bag);
    const price = parseFloat(variant.price) || 0;
    return {
      handle: product.handle,
      title: product.title,
      url: SHOP_DOMAIN + product.url,
      variant_title: variant.title,
      size_cf: variant.size_cf,
      bags: bags,
      price: price,
      subtotal: bags * price,
      available: variant.available,
      role: rec_spec.role || '',
      note: rec_spec.note || '',
      oos: !variant.available,
      per_metric: `1 bag per ${rec_spec.sqft_per_bag} sq ft`,
    };
  }

  function resolvePerPlant(rec_spec, plant_count) {
    if (!rec_spec) return null;
    const product = findProduct(rec_spec.handle);
    const variant = pickVariant(product);
    if (!product || !variant) return null;
    const bags = (rec_spec.bags_per_plant || 1) * (plant_count || 1);
    const price = parseFloat(variant.price) || 0;
    return {
      handle: product.handle,
      title: product.title,
      url: SHOP_DOMAIN + product.url,
      variant_title: variant.title,
      size_cf: variant.size_cf,
      bags: bags,
      price: price,
      subtotal: bags * price,
      available: variant.available,
      role: rec_spec.role || '',
      note: rec_spec.note || '',
      oos: !variant.available,
    };
  }

  function buildPlan(answers) {
    const pt = PROJECT_TYPES[answers.projectType];
    if (!pt) return null;
    const volume_cf = pt.computeVolume(answers);
    const mapping = pt.mapping(answers);

    const recs = [];
    const primary = resolveRec(mapping.primary, volume_cf, { fallback_handle: mapping.fallback_primary && mapping.fallback_primary.handle });
    if (primary) recs.push(primary);

    if (mapping.secondary) {
      const secondary = resolveRec(mapping.secondary, volume_cf);
      if (secondary) recs.push(secondary);
    }
    if (mapping.secondary_per_sqft && answers.sqft) {
      const r = resolvePerSqft(mapping.secondary_per_sqft, answers.sqft);
      if (r) recs.push(r);
    }
    if (mapping.secondary_per_plant) {
      const r = resolvePerPlant(mapping.secondary_per_plant, 1);
      if (r) recs.push(r);
    }

    const total = recs.reduce((s, r) => s + (r.subtotal || 0), 0);

    return {
      projectType: pt,
      answers: Object.assign({}, answers),
      volume_cf: volume_cf,
      recs: recs,
      why: mapping.why || '',
      total: total,
      generated_at: new Date(),
    };
  }

  // -------- Rendering --------
  const app = () => document.getElementById('soil-calculator-app');

  function h(tag, attrs, children) {
    const el = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        if (k === 'class') el.className = attrs[k];
        else if (k.startsWith('on')) el.addEventListener(k.slice(2), attrs[k]);
        else if (k === 'html') el.innerHTML = attrs[k];
        else el.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(c => {
        if (c == null || c === false) return;
        el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return el;
  }

  function render() {
    if (STATE.step === 'project') return renderProjectStep();
    if (STATE.step === 'details') return renderDetailsStep();
    if (STATE.step === 'results') return renderResultsStep();
  }

  function renderProjectStep() {
    const root = app();
    root.innerHTML = '';
    root.appendChild(h('p', { class: 'sc-step-meta' }, 'Step 1 of 2'));
    root.appendChild(h('h2', { class: 'sc-q' }, 'What\'s your project?'));
    root.appendChild(h('p', { class: 'sc-help' }, 'Pick the option that best matches what you\'re trying to do.'));

    const grid = h('div', { class: 'sc-cards' });
    Object.values(PROJECT_TYPES).forEach(pt => {
      const card = h('button', {
        class: 'sc-card' + (STATE.answers.projectType === pt.id ? ' is-active' : ''),
        type: 'button',
        onclick: () => {
          STATE.answers.projectType = pt.id;
          // Reset details-specific answers when project changes
          Object.assign(STATE.answers, pt.defaults || {});
          STATE.step = 'details';
          render();
        },
      }, [
        h('div', { class: 'sc-card__t' }, pt.label),
        h('div', { class: 'sc-card__d' }, pt.desc),
      ]);
      grid.appendChild(card);
    });
    root.appendChild(grid);
  }

  function numField(label, key, hint, step) {
    const wrap = h('div', { class: 'sc-field' });
    const lbl = h('label', { class: 'sc-field__label' }, label);
    if (hint) {
      const span = h('span', { class: 'sc-field__hint' }, hint);
      lbl.appendChild(span);
    }
    wrap.appendChild(lbl);
    const input = h('input', {
      type: 'number',
      min: '0',
      step: step || '1',
      value: STATE.answers[key] != null ? STATE.answers[key] : '',
      oninput: (e) => {
        const v = e.target.value;
        STATE.answers[key] = v === '' ? null : parseFloat(v);
        updateVolumeReadout();
        updateNextButton();
      },
    });
    wrap.appendChild(input);
    return wrap;
  }

  function pillRow(key, options) {
    const row = h('div', { class: 'sc-pills' });
    options.forEach(opt => {
      const pill = h('button', {
        class: 'sc-pill' + (STATE.answers[key] === opt.value ? ' is-active' : ''),
        type: 'button',
        onclick: () => {
          STATE.answers[key] = opt.value;
          render();
        },
      }, opt.label);
      row.appendChild(pill);
    });
    return row;
  }

  function sectionLabel(text, hint) {
    const wrap = h('div', { class: 'sc-field__label', style: 'margin: 8px 0 12px;' }, text);
    if (hint) wrap.appendChild(h('span', { class: 'sc-field__hint' }, hint));
    return wrap;
  }

  function renderDetailsStep() {
    const a = STATE.answers;
    const pt = PROJECT_TYPES[a.projectType];
    const root = app();
    root.innerHTML = '';
    root.appendChild(h('p', { class: 'sc-step-meta' }, 'Step 2 of 2'));
    root.appendChild(h('h2', { class: 'sc-q' }, pt.label));
    root.appendChild(h('p', { class: 'sc-help' }, 'Tell us the size and details. We\'ll handle the math.'));

    // Sub-choice (grow_what, soil_issue, etc.) FIRST so it gates the inputs.
    if (pt.needsGrowWhat) {
      root.appendChild(sectionLabel('What are you growing?'));
      root.appendChild(pillRow('grow_what', [
        { value: 'veg', label: 'Veggies & herbs' },
        { value: 'flower', label: 'Flowers & ornamentals' },
        { value: 'mixed', label: 'Mixed' },
      ]));
    }
    if (pt.needsSoilIssue) {
      root.appendChild(sectionLabel('What\'s the problem with the soil?'));
      root.appendChild(pillRow('soil_issue', [
        { value: 'clay', label: 'Heavy clay' },
        { value: 'sandy', label: 'Sandy / drains too fast' },
        { value: 'nutrients', label: 'Just needs nutrients' },
      ]));
    }
    if (pt.needsTopdressGoal) {
      root.appendChild(sectionLabel('What are you spreading?'));
      root.appendChild(pillRow('topdress_goal', [
        { value: 'compost', label: 'Compost' },
        { value: 'topsoil', label: 'Topsoil (filling, leveling)' },
        { value: 'premium_compost', label: 'Premium compost' },
        { value: 'manure', label: 'Steer manure' },
      ]));
    }
    if (pt.needsMulchUse) {
      root.appendChild(sectionLabel('Which mulch?'));
      root.appendChild(pillRow('mulch_use', [
        { value: 'hemlock', label: 'Hemlock (our favorite)' },
        { value: 'budget', label: 'Budget bark mulch' },
      ]));
    }
    if (pt.needsSpecialtyKind) {
      root.appendChild(sectionLabel('Which specialty mix?'));
      root.appendChild(pillRow('specialty_kind', [
        { value: 'orchid', label: 'Orchids' },
        { value: 'cactus', label: 'Cactus & succulents' },
        { value: 'cactus_citrus_palm', label: 'Palm / citrus / cactus' },
        { value: 'african_violet', label: 'African violets' },
        { value: 'seed_starting', label: 'Seed starting' },
      ]));
    }
    if (pt.needsPlantPotSize) {
      root.appendChild(sectionLabel('Is it an acid-loving plant?', 'Rhody, blueberry, camellia, azalea, etc.'));
      root.appendChild(pillRow('hole_kind', [
        { value: 'general', label: 'No, general tree or shrub' },
        { value: 'acid', label: 'Yes, acid-lover' },
      ]));
    }

    // Now the numeric inputs that depend on the sub-choice.
    const fields = h('div', { class: 'sc-fields' });

    if (pt.id === 'raised_bed_new' || pt.id === 'raised_bed_topup') {
      fields.appendChild(numField('Length', 'length_ft', '(feet)'));
      fields.appendChild(numField('Width', 'width_ft', '(feet)'));
      fields.appendChild(numField(pt.id === 'raised_bed_topup' ? 'New soil to add' : 'Depth', 'depth_in', '(inches)'));
    } else if (pt.id === 'in_ground_amend') {
      fields.appendChild(numField('Bed size', 'sqft', '(square feet)'));
    } else if (pt.id === 'topdress') {
      fields.appendChild(numField('Area', 'sqft', '(square feet)'));
      fields.appendChild(numField('Depth', 'depth_in', '(inches)', '0.5'));
    } else if (pt.id === 'pots') {
      root.appendChild(sectionLabel('How do you know the pot size?'));
      root.appendChild(pillRow('pot_input_method', [
        { value: 'dims', label: 'I\'ll measure inches' },
        { value: 'gallons', label: 'I know the gallon size' },
      ]));
      if (a.pot_input_method === 'gallons') {
        fields.appendChild(numField('Pot size', 'pot_gallons', '(gallons)', '0.5'));
      } else {
        fields.appendChild(numField('Pot diameter (top)', 'pot_diameter_in', '(inches)'));
        fields.appendChild(numField('Pot height', 'pot_height_in', '(inches)'));
      }
      fields.appendChild(numField('How many pots?', 'pot_count', ''));
      root.appendChild(fields);
      root.appendChild(sectionLabel('Any special needs?'));
      root.appendChild(pillRow('pot_style', [
        { value: 'standard', label: 'Standard pots' },
        { value: 'moisture', label: 'Hanging baskets / sunny spots' },
        { value: 'premium', label: 'Premium for showcase pots' },
      ]));
    } else if (pt.id === 'planting_hole') {
      fields.appendChild(numField('Plant size', 'plant_pot_size', '(gallons)'));
    } else if (pt.id === 'mulch') {
      fields.appendChild(numField('Area', 'sqft', '(square feet)'));
      fields.appendChild(numField('Depth', 'depth_in', '(inches, 2 for beds, 3 for paths)', '0.5'));
    } else if (pt.id === 'specialty') {
      fields.appendChild(numField('How many pots?', 'pot_count', ''));
    }

    // 'pots' already appended fields above so its order makes sense.
    if (fields.children.length && pt.id !== 'pots') root.appendChild(fields);

    // Live volume readout
    const vr = h('div', { class: 'sc-volume-readout', id: 'sc-volume-readout', style: 'display:none' });
    root.appendChild(vr);

    // Nav
    const nav = h('div', { class: 'sc-nav' });
    nav.appendChild(h('button', {
      class: 'sc-btn sc-btn--ghost',
      type: 'button',
      onclick: () => { STATE.step = 'project'; render(); },
    }, 'Back'));
    const nextBtn = h('button', {
      class: 'sc-btn sc-btn--primary',
      type: 'button',
      id: 'sc-next',
      onclick: () => {
        if (!detailsReady()) return;
        STATE.plan = buildPlan(STATE.answers);
        STATE.step = 'results';
        render();
      },
    }, 'Calculate my soil');
    nav.appendChild(nextBtn);
    root.appendChild(nav);

    updateVolumeReadout();
    updateNextButton();
  }

  function detailsReady() {
    const a = STATE.answers;
    const pt = PROJECT_TYPES[a.projectType];
    if (!pt) return false;
    if (pt.needsGrowWhat && !a.grow_what) return false;
    if (pt.needsSoilIssue && !a.soil_issue) return false;
    if (pt.needsTopdressGoal && !a.topdress_goal) return false;
    if (pt.needsMulchUse && !a.mulch_use) return false;
    if (pt.needsSpecialtyKind && !a.specialty_kind) return false;
    if (pt.needsPlantPotSize && !a.hole_kind) return false;
    if (pt.id === 'raised_bed_new' || pt.id === 'raised_bed_topup') {
      return a.length_ft > 0 && a.width_ft > 0 && a.depth_in > 0;
    }
    if (pt.id === 'in_ground_amend') return a.sqft > 0;
    if (pt.id === 'topdress') return a.sqft > 0 && a.depth_in > 0;
    if (pt.id === 'pots') {
      if (a.pot_input_method === 'gallons') return a.pot_gallons > 0 && a.pot_count > 0;
      return a.pot_diameter_in > 0 && a.pot_height_in > 0 && a.pot_count > 0;
    }
    if (pt.id === 'planting_hole') return a.plant_pot_size > 0;
    if (pt.id === 'mulch') return a.sqft > 0 && a.depth_in > 0;
    if (pt.id === 'specialty') return a.pot_count > 0;
    return false;
  }

  function updateVolumeReadout() {
    const vr = document.getElementById('sc-volume-readout');
    if (!vr) return;
    if (!detailsReady()) { vr.style.display = 'none'; return; }
    const pt = PROJECT_TYPES[STATE.answers.projectType];
    const cf = pt.computeVolume(STATE.answers);
    vr.style.display = 'block';
    vr.innerHTML = 'You\'ll need about <strong>' + fmt(Math.round(cf * 10) / 10) + ' cubic feet</strong> of soil for this project.';
  }

  function updateNextButton() {
    const btn = document.getElementById('sc-next');
    if (!btn) return;
    btn.disabled = !detailsReady();
  }

  function renderResultsStep() {
    const root = app();
    root.innerHTML = '';
    const plan = STATE.plan;
    const pt = plan.projectType;

    // Summary block
    const summary = h('div', { class: 'sc-summary' });
    summary.appendChild(h('div', { class: 'sc-summary__row' }, [
      h('div', { class: 'sc-summary__item' }, [document.createTextNode('Project: '), h('strong', null, pt.label)]),
      h('div', { class: 'sc-summary__item' }, [document.createTextNode('Details: '), h('strong', null, pt.summary(plan.answers))]),
      h('div', { class: 'sc-summary__item' }, [document.createTextNode('Soil needed: '), h('strong', null, fmt(Math.round(plan.volume_cf * 10) / 10) + ' cu ft')]),
    ]));
    summary.appendChild(h('div', { class: 'sc-summary__total' }, [
      h('strong', null, '$' + plan.total.toFixed(2)),
      document.createTextNode(' estimated total (' + plan.recs.reduce((s, r) => s + r.bags, 0) + ' bags)'),
    ]));
    root.appendChild(summary);

    // Recommendations
    plan.recs.forEach(r => root.appendChild(renderRec(r)));

    // Why
    if (plan.why) {
      const why = h('div', { class: 'sc-why' }, [
        h('strong', null, 'Why this combo: '),
        document.createTextNode(plan.why),
      ]);
      root.appendChild(why);
    }

    // PDF teaser
    const teaser = h('div', { class: 'sc-pdf-teaser' });
    teaser.appendChild(h('h3', { class: 'sc-pdf-teaser__h' }, 'Bring this list to the nursery'));
    teaser.appendChild(h('p', { class: 'sc-pdf-teaser__sub' }, 'Download a printable shopping list with your bag counts, prices, and our address.'));
    teaser.appendChild(h('button', {
      class: 'sc-btn sc-btn--primary',
      type: 'button',
      onclick: () => generatePDF(),
    }, 'Download my shopping list'));
    root.appendChild(teaser);

    // Disclaimer
    root.appendChild(h('div', { class: 'sc-disclaimer' }, [
      h('strong', null, 'Heads up: '),
      document.createTextNode('Prices and stock are from the day this list was generated. Call us at ' + CONTACT.phone + ' if you want to confirm before driving over.'),
    ]));

    // Bottom actions
    const actions = h('div', { class: 'sc-actions-bottom' });
    actions.appendChild(h('button', {
      class: 'sc-btn sc-btn--ghost',
      type: 'button',
      onclick: () => { STATE.step = 'project'; render(); },
    }, 'Start over'));
    actions.appendChild(h('button', {
      class: 'sc-btn sc-btn--ghost',
      type: 'button',
      onclick: () => { STATE.step = 'details'; render(); },
    }, 'Edit my answers'));
    root.appendChild(actions);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function renderRec(r) {
    const card = h('div', { class: 'sc-rec' + (r.oos ? ' sc-rec--oos' : '') });
    if (r.role) card.appendChild(h('span', { class: 'sc-rec__role' }, r.role));
    card.appendChild(h('h3', { class: 'sc-rec__title' }, [
      h('a', { href: r.url, target: '_blank', rel: 'noopener' }, r.title),
    ]));
    card.appendChild(h('p', { class: 'sc-rec__variant' }, r.variant_title + ' (' + fmt(r.size_cf) + ' cu ft per bag)'));

    const math = h('div', { class: 'sc-rec__math' });
    math.appendChild(h('span', { class: 'sc-rec__bags' }, r.bags + ' bag' + (r.bags === 1 ? '' : 's')));
    if (r.per_metric) {
      math.appendChild(h('span', null, r.per_metric));
    } else if (r.cf_covered != null) {
      math.appendChild(h('span', null, 'covers ' + fmt(Math.round(r.cf_covered * 10) / 10) + ' cu ft'));
    }
    const price = h('span', { class: 'sc-rec__price' }, [
      document.createTextNode('$' + r.price.toFixed(2) + ' each ('),
      h('strong', null, '$' + r.subtotal.toFixed(2)),
      document.createTextNode(' total)'),
    ]);
    math.appendChild(price);
    card.appendChild(math);

    if (r.note) card.appendChild(h('p', { class: 'sc-rec__note' }, r.note));

    if (r.oos) {
      card.appendChild(h('div', { class: 'sc-rec__oos-note' }, [
        h('strong', null, 'Currently out of stock. '),
        document.createTextNode('We still recommend it. Call ' + CONTACT.phone + ' for an ETA or ask about a substitute when you visit.'),
      ]));
    } else if (r.fallback_used) {
      card.appendChild(h('div', { class: 'sc-rec__oos-note' }, [
        h('strong', null, 'Note: '),
        document.createTextNode('Our first-choice product is out of stock. This is our recommended substitute.'),
      ]));
    }

    return card;
  }

  // -------- PDF generation --------
  function generatePDF() {
    if (!window.jspdf) {
      alert('PDF tool is still loading. Please try again in a moment.');
      return;
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 48;
    let y = M;

    const COLOR_DEEP = '#3B2B75';
    const COLOR_BODY = '#2b2b2b';
    const COLOR_MUTED = '#666666';
    const COLOR_GREEN = '#5d8a3a';
    const COLOR_CREAM = '#FAF5E8';

    // Header band
    doc.setFillColor(COLOR_CREAM);
    doc.rect(0, 0, W, 100, 'F');
    if (LOGO_URL) {
      try { doc.addImage(LOGO_URL, 'JPEG', M, 24, 60, 60); } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(COLOR_DEEP);
    doc.text('Your Soil Shopping List', M + 80, 50);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOR_MUTED);
    doc.text('Dragonfly Farm & Nursery, Langlois, Oregon', M + 80, 68);
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(date, W - M, 68, { align: 'right' });

    y = 130;

    const plan = STATE.plan;
    const pt = plan.projectType;

    // Project summary
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(COLOR_BODY);
    doc.text('Project:', M, y);
    doc.setFont('helvetica', 'normal');
    doc.text(pt.label, M + 70, y);
    y += 16;
    doc.setFont('helvetica', 'bold');
    doc.text('Details:', M, y);
    doc.setFont('helvetica', 'normal');
    const detailsWrapped = doc.splitTextToSize(pt.summary(plan.answers), W - M - 70 - M);
    doc.text(detailsWrapped, M + 70, y);
    y += detailsWrapped.length * 13 + 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Volume:', M, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fmt(Math.round(plan.volume_cf * 10) / 10) + ' cubic feet of soil', M + 70, y);
    y += 24;

    // Why
    if (plan.why) {
      doc.setFillColor(COLOR_CREAM);
      const whyLines = doc.splitTextToSize(plan.why, W - 2 * M - 20);
      const whyHeight = whyLines.length * 12 + 16;
      doc.rect(M, y - 4, W - 2 * M, whyHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_DEEP);
      doc.text('Why this combo', M + 10, y + 10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLOR_BODY);
      doc.text(whyLines, M + 10, y + 24);
      y += whyHeight + 16;
    }

    // Recommendations
    doc.setFillColor(COLOR_GREEN);
    doc.rect(M, y - 14, 4, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(COLOR_DEEP);
    doc.text('What to buy', M + 12, y);
    y += 18;

    plan.recs.forEach(r => {
      if (y > H - 130) { doc.addPage(); y = M; }

      // Bag count + product title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(COLOR_DEEP);
      doc.text(r.bags + ' ×  ' + r.title, M, y);
      y += 14;

      // Variant + price
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_BODY);
      const priceLine = r.variant_title + '   |   $' + r.price.toFixed(2) + ' each   |   $' + r.subtotal.toFixed(2) + ' total';
      doc.text(priceLine, M + 12, y);
      y += 13;

      // Role
      if (r.role) {
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(COLOR_MUTED);
        doc.text(r.role, M + 12, y);
        y += 13;
      }

      // Note
      if (r.note) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(COLOR_BODY);
        const noteLines = doc.splitTextToSize(r.note, W - 2 * M - 12);
        doc.text(noteLines, M + 12, y);
        y += noteLines.length * 12;
      }

      // OOS flag
      if (r.oos) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#b9933e');
        doc.text('NOTE: This product was out of stock when the list was made.', M + 12, y);
        y += 13;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(COLOR_MUTED);
        doc.text('Call ' + CONTACT.phone + ' for an ETA or substitute.', M + 12, y);
        y += 13;
      }
      y += 10;
    });

    // Total
    if (y > H - 90) { doc.addPage(); y = M; }
    doc.setDrawColor('#d4ccae');
    doc.line(M, y, W - M, y);
    y += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(COLOR_DEEP);
    doc.text('Estimated total:', M, y);
    doc.text('$' + plan.total.toFixed(2), W - M, y, { align: 'right' });
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLOR_MUTED);
    doc.text(plan.recs.reduce((s, r) => s + r.bags, 0) + ' bags total. Prices may vary. Sales tax not included.', M, y);
    y += 28;

    // Footer (contact)
    if (y > H - 130) { doc.addPage(); y = M; }
    doc.setFillColor(COLOR_CREAM);
    doc.rect(M, y, W - 2 * M, 90, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(COLOR_DEEP);
    doc.text('Dragonfly Farm & Nursery', M + 14, y + 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOR_BODY);
    doc.text(CONTACT.address || '', M + 14, y + 38);
    doc.text(CONTACT.phone || '', M + 14, y + 52);
    doc.text(CONTACT.email || '', M + 14, y + 66);
    doc.setTextColor(COLOR_MUTED);
    doc.setFontSize(9);
    doc.text(CONTACT.hours || '', M + 14, y + 80);
    doc.text(CONTACT.website || '', W - M - 14, y + 80, { align: 'right' });

    doc.save('my-dragonfly-soil-list.pdf');
  }

  // -------- Boot --------
  function boot() {
    const root = app();
    if (!root) return;
    root.innerHTML = '<div class="sc-loading">Loading the calculator...</div>';
    fetch(DATA_URL)
      .then(r => r.json())
      .then(d => {
        STATE.data = d;
        render();
      })
      .catch(err => {
        root.innerHTML = '<div class="sc-loading">Could not load product data. Please refresh and try again.</div>';
        console.error('Soil calculator data load failed', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
